import { canonicaCategoria } from "@/lib/categorie";
import { RULLO_HREF } from "@/lib/constants";
import { MACRO_REGIONS, SUB_REGIONS, isStrictCountry } from "@/lib/regioni";
import { VISTA_RICERCA, hrefRullo, leggiStato } from "@/lib/rullo";
import { articleHref, slugify } from "@/lib/slug";
import type { NewsItem, TipoDerivato } from "@/lib/types";

/**
 * I conti della prima pagina del 15/10/2026 (uploads/Prima pagina-html.zip).
 *
 * Tutto quello che le fasce mostrano e che si ricava dai dispacci già
 * chiesti in app/page.tsx: le righe del «Flusso informativo», i temi con
 * il loro conteggio, i continenti e i Paesi più attivi. Nessuna richiesta
 * in più a monte: a monte non c'è un'aggregazione (vedi learnings,
 * «Conteggi: niente aggregazione»), quindi si conta sulla lista del rullo
 * che la pagina chiede comunque.
 *
 * Modulo senza direttiva: lo leggono la pagina (server) e due fasce
 * client (<Regioni>, <Atlante>). Una costante esportata da un file
 * "use client" e letta dal server non è il suo valore.
 */

/* ───────────────────────────── i temi ───────────────────────────── */

/** Le icone dei temi, disegnate in components/home/IconaTema.tsx sulle
 *  forme del disegno (un tratto a 1,5 con un dettaglio pieno nel rame). */
export type IconaTema =
  | "energia"
  | "finanza"
  | "politica"
  | "esteri"
  | "trasporti"
  | "tecnologia"
  | "difesa"
  | "impresa"
  | "commercio"
  | "agricoltura"
  | "sanzioni"
  | "notizia";

/** Il tono di ogni icona: i quattro toni dei temi del disegno sono
 *  `--oi-home-tema-*` (globals.css), che nel chiaro valgono i `--p-*` già
 *  misurati (energia ocra, finanza verde, politica viola, trasporti
 *  petrolio) e nello scuro i valori del disegno scuro; impresa e sanzioni
 *  hanno il rame del segnale, come nel disegno. */
export const TONO_ICONA: Record<IconaTema, string> = {
  energia: "var(--oi-home-tema-energia)",
  agricoltura: "var(--oi-home-tema-energia)",
  finanza: "var(--oi-home-tema-finanza)",
  commercio: "var(--oi-home-tema-finanza)",
  politica: "var(--oi-home-tema-politica)",
  esteri: "var(--oi-home-tema-politica)",
  difesa: "var(--oi-home-tema-politica)",
  trasporti: "var(--oi-home-tema-trasporti)",
  tecnologia: "var(--oi-home-tema-trasporti)",
  impresa: "var(--color-segnale)",
  sanzioni: "var(--color-segnale)",
  notizia: "var(--color-sommesso)",
};

/** L'icona di una categoria a monte, già canonica. Le categorie sono
 *  scritte a mano in redazione (una quindicina sui dispacci recenti, vedi
 *  lib/categorie.ts): quelle che il disegno non prevede ricadono sul
 *  foglio del dispaccio, che non dice niente di più di quello che c'è. */
const ICONA_CATEGORIA: Record<string, IconaTema> = {
  energia: "energia",
  petrolio: "energia",
  gas: "energia",
  commodities: "agricoltura",
  agricoltura: "agricoltura",
  "materie prime": "agricoltura",
  esteri: "esteri",
  diplomazia: "esteri",
  politica: "politica",
  economia: "finanza",
  finanza: "finanza",
  "settore bancario": "finanza",
  investimenti: "finanza",
  infrastruttura: "trasporti",
  infrastrutture: "trasporti",
  trasporti: "trasporti",
  logistica: "trasporti",
  "information technology": "tecnologia",
  tecnologia: "tecnologia",
  "industria militare": "difesa",
  difesa: "difesa",
  sicurezza: "difesa",
  "fare impresa": "impresa",
  industria: "impresa",
  commercio: "commercio",
  dogane: "commercio",
  sanzioni: "sanzioni",
};

export function iconaCategoria(cat: string | null | undefined): IconaTema {
  const c = canonicaCategoria(cat).toLocaleLowerCase("it");
  return ICONA_CATEGORIA[c] ?? "notizia";
}

/** I cinque temi sotto osservazione della ricerca in cima alla pagina.
 *  Prima erano `TEMI_HOME` in lib/constants.ts, quattro su cinque sulla
 *  ricerca per testo perché allora le categorie a monte erano vuote; dal
 *  30/09 i dispacci del rullo hanno quasi tutti la categoria (Energia,
 *  Esteri, Trasporti, Finanza sono fra le più usate), quindi i temi che
 *  sono una categoria atterrano sul rullo filtrato per categoria, che è
 *  la classificazione della redazione. «Sanzioni» come categoria non
 *  esiste: resta sulla ricerca per testo, ristretta al rullo. */
export type TemaPrimaPagina = { etichetta: string; icona: IconaTema; href: string };

const sulRullo = (param: "cat" | "q", valore: string) =>
  `${RULLO_HREF}?${param}=${encodeURIComponent(valore)}`;

export const TEMI_PRIMA_PAGINA: TemaPrimaPagina[] = [
  { etichetta: "Energia", icona: "energia", href: sulRullo("cat", "Energia") },
  { etichetta: "Sanzioni e rischio", icona: "sanzioni", href: sulRullo("q", "sanzioni") },
  { etichetta: "Finanza e investimenti", icona: "finanza", href: sulRullo("cat", "Finanza") },
  { etichetta: "Politica ed esteri", icona: "esteri", href: sulRullo("cat", "Esteri") },
  { etichetta: "Trasporti e infrastrutture", icona: "trasporti", href: sulRullo("cat", "Trasporti") },
];

/** La categoria a monte dei pezzi per chi fa impresa all'estero, che
 *  alimenta la fascia «Per le PMI». Il tipo `pmi` a monte ha tre dispacci
 *  in tutto e nessuno recente; la categoria ne aveva sei sui cento più
 *  recenti del 15/10/2026. */
export const CATEGORIA_PMI = "Fare impresa";

/* ───────────────────────────── le ore ───────────────────────────── */

/** Le ore e i giorni si scrivono sul server, nel fuso di Roma: il
 *  risultato dipende dal solo istante del dispaccio e non dall'orologio,
 *  quindi l'HTML è lo stesso per tutti e non c'è niente da idratare. */
const FUSO = "Europe/Rome";
const fmtOra = new Intl.DateTimeFormat("it-IT", { timeZone: FUSO, hour: "2-digit", minute: "2-digit" });
const fmtGiorno = new Intl.DateTimeFormat("it-IT", { timeZone: FUSO, day: "numeric", month: "short" });
const fmtChiave = new Intl.DateTimeFormat("en-CA", { timeZone: FUSO, year: "numeric", month: "2-digit", day: "2-digit" });

export const oraRoma = (ts: number) => fmtOra.format(new Date(ts));
const chiaveGiorno = (ts: number) => fmtChiave.format(new Date(ts));

/* ─────────────────────────── il flusso ──────────────────────────── */

/** Quante righe ha il «Flusso informativo»: le prime della lista del
 *  rullo. Il disegno ne mostra quindici; venti tengono la pista piena
 *  per tutto il giro anche sul telefono, dove le righe sono più alte. */
export const RIGHE_FLUSSO = 20;

/** Quante righe del flusso si vedono sempre, qualunque sia l'altezza della
 *  spalla accanto (vedi `.oi-pp-flusso` in globals.css): dieci, quante ne
 *  mostrava la fascia nei giorni normali. Le altre fino a `RIGHE_FLUSSO`
 *  compaiono solo se la spalla è più alta. Dal 25/10/2026, su richiesta
 *  del committente: le notizie devono restare quelle anche quando a monte
 *  non arriva niente di nuovo. */
export const RIGHE_FISSE = 10;

/** Una riga del «Flusso informativo»: i soli dati che la riga rende. */
export type RigaFlusso = {
  id: string;
  href: string;
  /** «15:27», o «14 ott» per i dispacci di un giorno diverso da quello
   *  del riferimento: il primo della lista, o il dispaccio più recente del
   *  rullo per le liste che stanno accanto al flusso (PMI). Non
   *  l'orologio: così la stringa non dipende da quando si guarda. */
  quando: string;
  iso: string;
  paese: string | null;
  categoria: string;
  icona: IconaTema;
  titolo: string;
  /** Il filone (`occhiello`), com'è. Fino al 15/10/2026 portava «Caso · »
   *  o «Report · » davanti; ora quello lo dice il segno del caso accanto
   *  (`tipoDerivato` e `tappe`, resi da `TappeCaso`). */
  filone: string | null;
  /** Notizia, caso o report, dalla cronologia (`NewsItem.tipoDerivato`). */
  tipoDerivato: TipoDerivato | null;
  /** Le tappe del filone, per «Il caso · N tappe» e i rombi. */
  tappe: number | null;
};

export function righeFlusso(items: NewsItem[], riferimento?: number): RigaFlusso[] {
  const primo = riferimento ?? items[0]?.time;
  const oggi = primo !== undefined ? chiaveGiorno(primo) : "";
  return items.map((d) => {
    const cat = canonicaCategoria(d.category);
    const fil = d.occhiello?.trim() || null;
    return {
      id: String(d.id),
      href: articleHref(d),
      quando: chiaveGiorno(d.time) === oggi ? oraRoma(d.time) : fmtGiorno.format(new Date(d.time)),
      iso: new Date(d.time).toISOString(),
      paese: d.entities.places[0] ?? null,
      categoria: cat,
      icona: iconaCategoria(cat),
      titolo: d.title,
      filone: fil,
      tipoDerivato: d.tipoDerivato ?? null,
      tappe: d.tappe ?? null,
    };
  });
}

/* ──────────────────── la finestra dei conteggi ──────────────────── */

/** Su quanti dispacci si contano temi e continenti. Il disegno dice
 *  «Oggi · 64 dispacci»; a monte il ritmo vero è di una ventina al
 *  giorno (21 nelle 24 ore misurate il 15/10/2026), quindi «oggi» farebbe
 *  barre da uno e due. La finestra è di tre giorni, e la si dichiara
 *  solo se la lista la copre per intero: se nelle 72 ore i dispacci sono
 *  più di quelli chiesti, l'etichetta dice quanti sono, non un periodo
 *  che non è stato contato tutto. */
export const ORE_FINESTRA = 72;

/** Su quanti dispacci almeno si contano temi e continenti. Nei giorni
 *  fermi (un fine settimana, 25/10/2026) le 72 ore ne contenevano pochi, i
 *  temi si riducevano a tre o quattro e la colonna accanto al flusso si
 *  accorciava: allora si contano gli ultimi quaranta, qualunque sia la loro
 *  data, e l'etichetta lo dice. */
export const MINIMO_FINESTRA = 40;

export type Finestra = { items: NewsItem[]; etichetta: string };

export function finestraConteggi(items: NewsItem[], adesso: number, richiesti: number): Finestra {
  const da = adesso - ORE_FINESTRA * 3600_000;
  const dentro = items.filter((d) => d.time >= da);
  if (dentro.length < MINIMO_FINESTRA && items.length > dentro.length) {
    const ultimi = items.slice(0, MINIMO_FINESTRA);
    return { items: ultimi, etichetta: `Ultimi ${ultimi.length} dispacci` };
  }
  const coperta = items.length < richiesti || (items.at(-1)?.time ?? adesso) < da;
  return {
    items: dentro,
    etichetta: coperta ? `Ultime ${ORE_FINESTRA} ore` : `Ultimi ${dentro.length} dispacci`,
  };
}

/** I temi della finestra, per il riquadro «Filtra per tema» accanto al
 *  flusso: categoria canonica, icona e quanti dispacci, dal più pieno.
 *  Le categorie vuote (la stringa vuota a monte) non fanno un tema. */
export type VoceTema = { categoria: string; icona: IconaTema; quanti: number; href: string };

export function contaTemi(items: NewsItem[], quante = 10): VoceTema[] {
  const m = new Map<string, number>();
  for (const d of items) {
    const c = canonicaCategoria(d.category);
    if (c) m.set(c, (m.get(c) ?? 0) + 1);
  }
  return [...m.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "it"))
    .slice(0, quante)
    .map(([categoria, quanti]) => ({
      categoria,
      icona: iconaCategoria(categoria),
      quanti,
      href: sulRullo("cat", categoria),
    }));
}

/* ─────────────────────────── i continenti ─────────────────────────── */

export type PaeseAttivo = {
  nome: string;
  href: string;
  quanti: number;
  /** Il titolo del suo dispaccio più recente nella finestra. */
  ultimo: string;
  /** Quanti dispacci ha in archivio, tutti gli anni (`countCases` per
   *  titolo, la stessa domanda e la stessa cache di /paesi/[slug]).
   *  `undefined` finché non arriva, `null` se la richiesta è caduta. */
  archivio?: number | null;
};

export type ContinenteAttivo = {
  slug: string;
  nome: string;
  /** I dispacci della finestra che toccano il continente (un dispaccio
   *  legato a più Paesi dello stesso continente conta una volta). */
  quanti: number;
  paesi: PaeseAttivo[];
  /** Quando la settimana non ha dispacci sul continente, i Paesi sono
   *  quelli degli ultimi dispacci pubblicati, più vecchi: qui la data del
   *  più recente, già scritta nel fuso di Roma («12 ott»). `null` quando i
   *  Paesi sono della settimana. */
  vecchiDal?: string | null;
  /** Quanti dispacci ha il continente in archivio (i suoi Paesi, `$in`
   *  sul titolo). Come `PaeseAttivo.archivio`. */
  archivio?: number | null;
};

const norma = (s: string) => slugify(s);

/** Il continente di un nome a monte. La relation `countries` a monte non
 *  porta solo Paesi: porta anche aree («Europa», «Balcani», «Asia
 *  centrale»). Un'area che è il nome di un continente o di una sua
 *  sotto-regione (lib/regioni.ts) conta per quel continente; un Paese
 *  conta per il continente della sua sotto-regione. */
const CONTINENTE_DI = new Map<string, string>();
for (const m of MACRO_REGIONS) CONTINENTE_DI.set(norma(m.name), m.slug);
for (const s of SUB_REGIONS) {
  CONTINENTE_DI.set(norma(s.name), s.macroSlug);
  for (const c of s.countries) if (!CONTINENTE_DI.has(norma(c))) CONTINENTE_DI.set(norma(c), s.macroSlug);
}

export function contaContinenti(items: NewsItem[], paesiPerContinente = 7): ContinenteAttivo[] {
  const quanti = new Map<string, number>();
  /* Per Paese: quanti dispacci e il titolo del più recente (la lista
     arriva dal più recente, quindi il primo che si incontra). */
  const paesi = new Map<string, { slug: string; nome: string; quanti: number; ultimo: string }>();
  for (const d of items) {
    const toccati = new Set<string>();
    const visti = new Set<string>();
    for (const luogo of d.paesi ?? d.entities.places) {
      const k = norma(luogo);
      const cont = CONTINENTE_DI.get(k);
      if (!cont) continue;
      toccati.add(cont);
      if (!isStrictCountry(luogo) || visti.has(k)) continue;
      visti.add(k);
      const p = paesi.get(k) ?? { slug: cont, nome: luogo, quanti: 0, ultimo: d.title };
      p.quanti += 1;
      paesi.set(k, p);
    }
    for (const c of toccati) quanti.set(c, (quanti.get(c) ?? 0) + 1);
  }
  /* Dal più pieno, come nel disegno; a pari conto l'ordine di
     lib/regioni.ts. */
  return MACRO_REGIONS.map((m) => ({
    slug: m.slug,
    nome: m.name,
    quanti: quanti.get(m.slug) ?? 0,
    paesi: [...paesi.entries()]
      .filter(([, p]) => p.slug === m.slug)
      .sort((a, b) => b[1].quanti - a[1].quanti)
      .slice(0, paesiPerContinente)
      .map(([k, p]) => ({ nome: p.nome, href: `/paesi/${k}`, quanti: p.quanti, ultimo: p.ultimo })),
  })).sort((a, b) => b.quanti - a.quanti);
}

/** I Paesi veri di un continente, dalla tassonomia di lib/regioni.ts:
 *  per contare l'archivio del continente e per pescare gli ultimi dispacci
 *  quando la settimana è vuota. */
export function paesiDelContinente(slug: string): string[] {
  return [
    ...new Set(SUB_REGIONS.filter((s) => s.macroSlug === slug).flatMap((s) => s.countries).filter((c) => isStrictCountry(c))),
  ];
}

/** «Regioni e Paesi» dal 25/10/2026 (richiesta del committente: i Paesi
 *  devono dare il quadro della settimana, e quando non ci sono dati
 *  restano quelli vecchi). I conteggi sono della **settimana**, gli ultimi
 *  sette giorni; un continente che nella settimana non ha niente non resta
 *  vuoto: prende i Paesi dagli ultimi dispacci che lo toccano, `vecchi`
 *  (più vecchi della settimana, dalla lista del rullo o da `piuVecchi`, che
 *  la pagina chiede a parte per i continenti che nemmeno la lista
 *  raggiunge), e lo dice con la data (`vecchiDal`). Il conteggio della
 *  settimana resta zero: è il dato vero. */
export const GIORNI_SETTIMANA = 7;

export type QuadroSettimana = {
  continenti: ContinenteAttivo[];
  /** «Ultimi 7 giorni», o «Ultimi N dispacci» se la lista non copre la
   *  settimana intera. */
  etichetta: string;
  /** I dispacci della settimana contati. */
  quanti: number;
};

/** Il rullo senza filtri, ristretto agli ultimi sette giorni: la base dei
 *  link dei Paesi della settimana. */
const RULLO_SETTIMANA = { ...leggiStato({}, VISTA_RICERCA), date: "7d" as const };

export function quadroSettimana(
  items: NewsItem[],
  adesso: number,
  richiesti: number,
  piuVecchi: Record<string, NewsItem[]> = {}
): QuadroSettimana {
  const da = adesso - GIORNI_SETTIMANA * 24 * 3600_000;
  const settimana = items.filter((d) => d.time >= da);
  const coperta = items.length < richiesti || (items.at(-1)?.time ?? adesso) < da;
  const conti = contaContinenti(settimana);
  const continenti = conti.map((c) => {
    /* Un Paese della settimana porta ai dispacci che il numero accanto
       conta: il rullo con quel Paese e il periodo «Ultimi 7 giorni»
       (`?place=<nome>&date=7d`, la stessa finestra di `GIORNI_SETTIMANA`).
       Le voci di ripiego (`vecchiDal`) restano su /paesi/<slug>: sono
       fuori dalla settimana, e il filtro darebbe una lista vuota. */
    if (c.paesi.length > 0)
      return {
        ...c,
        paesi: c.paesi.map((p) => ({ ...p, href: hrefRullo(RULLO_SETTIMANA, { paese: p.nome }) })),
        vecchiDal: null,
      };
    const fonte = [...items.filter((d) => d.time < da), ...(piuVecchi[c.slug] ?? [])];
    const vecchio = contaContinenti(fonte).find((x) => x.slug === c.slug);
    if (!vecchio || vecchio.paesi.length === 0) return { ...c, vecchiDal: null };
    /* La data del più recente fra i dispacci che toccano i Paesi trovati. */
    const nomi = new Set(vecchio.paesi.map((p) => norma(p.nome)));
    const ultimo = fonte
      .filter((d) => (d.paesi ?? d.entities.places).some((l) => nomi.has(norma(l))))
      .reduce((m, d) => Math.max(m, d.time), 0);
    return { ...c, paesi: vecchio.paesi, vecchiDal: ultimo ? fmtGiorno.format(new Date(ultimo)) : null };
  });
  return {
    continenti,
    etichetta: coperta ? `Ultimi ${GIORNI_SETTIMANA} giorni` : `Ultimi ${settimana.length} dispacci`,
    quanti: settimana.length,
  };
}

/** Tutti i Paesi della tassonomia, per i suggerimenti del campo «Cerca un
 *  Paese». Solo Paesi veri, senza doppioni, in ordine alfabetico. */
export const PAESI_TASSONOMIA: string[] = [
  ...new Set(SUB_REGIONS.flatMap((s) => s.countries).filter((c) => isStrictCountry(c))),
].sort((a, b) => a.localeCompare(b, "it"));
